import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as loadable from 'react-loadable';
import debounce from 'lodash/debounce';
import { Notify, Loading } from 'ljit-react-components';
import { LoadingStatusEnum } from '../lib/enums';
import { markNewVersionDetected } from './new-version-signal';

const {
	LOADING,
	SUCCESS,
	FAILED,
} = LoadingStatusEnum;
const RESIZE_DEBOUNCE_MS = 150;

export function loadComponent(options) {
	return loadable(Object.assign({
		loading: LoadingWrapper,
	}, options, {
		loader: withRetryOnce(options.loader),
	}));
}

// 第一次 import 失敗先靜默重試一次（擋暫時性網路抖動），第二次才視為真失敗。
function withRetryOnce(loader) {
	return () => loader().catch(() => loader());
}

// Webpack 4 chunk 載入失敗的形狀：JS chunk 為 ChunkLoadError 名稱或「Loading chunk X failed」訊息；
// mini-css-extract-plugin 的 async CSS chunk 失敗則帶 code === 'CSS_CHUNK_LOAD_FAILED'、訊息為「Loading CSS chunk X failed」。
function isChunkLoadError(error) {
	return error.name === 'ChunkLoadError' ||
		error.code === 'CSS_CHUNK_LOAD_FAILED' ||
		/Loading (CSS )?chunk [\w-]+ failed/i.test(error.message || '');
}

function LoadingWrapper(prop) {
	const { error, retry } = prop;
	const { t } = useTranslation();

	useEffect(() => {
		if (error && isChunkLoadError(error)) {
			markNewVersionDetected();
		}
	}, [error]);

	if (process.env.NODE_ENV !== 'production' && error) {
		console.error(error);
	}

	if (!error) {
		return <Loading />;
	}

	// Chunk 失效 = 已發新版：提示交給 NewVersionNotice 的 modal，這裡不回無限 spinner
	if (isChunkLoadError(error)) {
		return null;
	}

	// 非 chunk 的真實載入錯誤：顯示重試按鈕，取代永遠轉圈
	return (
		<div className="load-failed">
			<button type="button" onClick={retry}>
				{t('页面加载失败，请重试')}
			</button>
		</div>
	);
}

export function useDebouncedResizeObserver(target, onMeasure) {
	useEffect(() => {
		const element = target instanceof Element ? target : target?.current;

		if (!element) {
			return undefined;
		}

		onMeasure();

		if (typeof ResizeObserver === 'undefined') {
			return undefined;
		}

		const debouncedMeasure = debounce(onMeasure, RESIZE_DEBOUNCE_MS);
		const resizeObserver = new ResizeObserver(debouncedMeasure);

		resizeObserver.observe(element);

		return () => {
			resizeObserver.disconnect();
			debouncedMeasure.cancel();
		};
	}, [target, onMeasure]);
}

export function usePrevious(value) {
	const ref = useRef();

	useEffect(() => {
		ref.current = value;
	});

	return ref.current;
}

export function usePreviousPersistent(value) {
	const ref = useRef({
		value: value,
		prev: null,
	});
	const current = ref.current.value;

	if (value !== current) {
		ref.current = {
			value: value,
			prev: current,
		};
	}

	return ref.current.prev;
}

export function useHover() {
	const [value, setValue] = useState(false);
	const ref = useRef(null);
	const handleMouseOver = () => setValue(true);
	const handleMouseOut = () => setValue(false);

	useEffect(
		() => {
			const node = ref.current;

			if (node) {
				node.addEventListener('mouseover', handleMouseOver);
				node.addEventListener('mouseout', handleMouseOut);

				return () => {
					node.removeEventListener('mouseover', handleMouseOver);
					node.removeEventListener('mouseout', handleMouseOut);
				};
			}
		}, [ref.current]);

	return [ref, value];
}

export function useKeyPress(targetKey) {
	const [keyPressed, setKeyPressed] = useState(false);

	function downHandler({ key }) {
		if (key === targetKey) {
			setKeyPressed(true);
		}
	}

	const upHandler = ({ key }) => {
		if (key === targetKey) {
			setKeyPressed(false);
		}
	};

	useEffect(() => {
		window.addEventListener('keydown', downHandler);
		window.addEventListener('keyup', upHandler);

		return () => {
			window.removeEventListener('keydown', downHandler);
			window.removeEventListener('keyup', upHandler);
		};
	}, []);

	return keyPressed;
}

export const useWindowDimensions = () => {
	const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

	useLayoutEffect(() => {
		const handleSize = () => {
			setWindowSize({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		};

		handleSize();

		window.addEventListener('resize', handleSize);

		return () => window.removeEventListener('resize', handleSize);
	}, []);

	return windowSize;
};

export const windowModeEnum = {
	MOBILE: 'mobile',
	TABLET: 'tablet',
	DESKTOP: 'desktop',
};

const {
	MOBILE,
	TABLET,
	DESKTOP,
} = windowModeEnum;

export const useWindowMode = (defaultMode = DESKTOP) => {
	const { width } = useWindowDimensions();
	const [windowMode, setWindowMode] = useState(defaultMode);
	// Refer app/styling/rwd.styl
	// $xs = 380px
	// $sm = 600px
	// $md = 900px
	// $lg = 1200px
	// $xl = 1536px

	useEffect(() => {
		if (width <= 600) {
			setWindowMode(MOBILE);
		} else if (width < 900) {
			setWindowMode(TABLET);
		} else {
			setWindowMode(DESKTOP);
		}
	}, [width]);

	return windowMode;
};

export function useLoadingStatus({
	loadingStatus,
	successMessage,
	failureMessage,
	onSuccess,
	onFailed,
	notifyDuration = 3000,
}) {
	const prevLoadingStatus = usePrevious(loadingStatus);

	useEffect(() => {
		if (LOADING === prevLoadingStatus && SUCCESS === loadingStatus) {
			if (successMessage && typeof successMessage === 'string') {
				Notify.successFill(successMessage, notifyDuration);
			}

			if (onSuccess && typeof onSuccess === 'function') {
				onSuccess();
			}
		} else if (LOADING === prevLoadingStatus && FAILED === loadingStatus) {
			if (failureMessage && typeof failureMessage === 'string') {
				Notify.error(failureMessage, notifyDuration);
			}

			if (onFailed && typeof onFailed === 'function') {
				onFailed();
			}
		}
	}, [
		prevLoadingStatus,
		loadingStatus,
		successMessage,
		failureMessage,
		onSuccess,
		onFailed,
		notifyDuration,
	]);
}
